import { useTranslate, useListContext } from 'react-admin';
import { FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { addDays, startOfDay } from 'date-fns';

const formatDate = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const dueDateFilterChoices = translate => {
    const choices = [
        { id: 'all', name: translate('common.all') },
        { id: 'overdue', name: translate('resources.serviceInspection.filters.overdue') },
        { id: 'upTo30Days', name: translate('resources.serviceInspection.filters.upTo30Days') },
        { id: 'from31To90Days', name: translate('resources.serviceInspection.filters.from31To90Days') },
    ];
    return choices;
};

export const DueDateFilter = ({ fromSource = 'dueDate_gte', toSource = 'dueDate_lte', ...props }) => {
    const translate = useTranslate();
    const { filterValues, setFilters } = useListContext();

    const fromSourceValue = filterValues[fromSource];
    const toSourceValue = filterValues[toSource];

    const currentValue = useMemo(() => {
        const hasDueDateFilters = toSourceValue || fromSourceValue;
        if (!hasDueDateFilters) {
            return 'all';
        }

        const today = startOfDay(new Date());
        const todayStr = formatDate(today);

        const lte = toSourceValue;
        const gte = fromSourceValue;

        if (lte === todayStr && !gte) {
            return 'overdue';
        } else if (gte === todayStr) {
            const endDate = addDays(today, 30);
            if (lte === formatDate(endDate)) {
                return 'upTo30Days';
            }
        } else if (gte) {
            const startDate = addDays(today, 31);
            const endDate = addDays(today, 90);
            if (gte === formatDate(startDate) && lte === formatDate(endDate)) {
                return 'from31To90Days';
            }
        }

        return 'all';
    }, [fromSourceValue, toSourceValue]);

    const handleChange = useCallback(
        event => {
            const value = event.target.value;
            const updatedFilters = { ...filterValues };
            const today = startOfDay(new Date());

            delete updatedFilters[fromSource];
            delete updatedFilters[toSource];

            if (value === 'overdue') {
                updatedFilters[toSource] = formatDate(today);
            } else if (value === 'upTo30Days') {
                const endDate = addDays(today, 30);
                updatedFilters[fromSource] = formatDate(today);
                updatedFilters[toSource] = formatDate(endDate);
            } else if (value === 'from31To90Days') {
                const startDate = addDays(today, 31);
                const endDate = addDays(today, 90);
                updatedFilters[fromSource] = formatDate(startDate);
                updatedFilters[toSource] = formatDate(endDate);
            }

            setFilters(updatedFilters, false);
        },
        [filterValues, setFilters, fromSource, toSource],
    );

    const choices = dueDateFilterChoices(translate);

    return (
        <FormControl className='radio-button-group' component='fieldset' {...props}>
            <RadioGroup value={currentValue} onChange={handleChange} row>
                {choices.map(choice => (
                    <FormControlLabel key={choice.id} value={choice.id} control={<Radio />} label={choice.name} />
                ))}
            </RadioGroup>
        </FormControl>
    );
};

DueDateFilter.propTypes = {
    fromSource: PropTypes.string,
    toSource: PropTypes.string,
};
